import ko from '../locales/ko.js';
import en from '../locales/en.js';
import ja from '../locales/ja.js';
import zh from '../locales/zh.js';
import vi from '../locales/vi.js';
import th from '../locales/th.js';
import es from '../locales/es.js';
import fr from '../locales/fr.js';
import de from '../locales/de.js';
import ru from '../locales/ru.js';

const RESOURCES = { ko, en, ja, zh, vi, th, es, fr, de, ru };
const DEFAULT_LANG = 'ko';

class LocaleService {
    constructor() {
        this.lang = DEFAULT_LANG;
        this.resource = ko;
    }

    /**
     * Initialize language from LocalStorage or Browser Settings
     */
    init() {
        try {
            const saved = localStorage.getItem('mirinae_lang');
            if (saved && RESOURCES[saved]) {
                this.lang = saved;
            } else {
                // Auto detect (e.g. "en-US" -> "en")
                const browser = navigator.language.split('-')[0];
                if (RESOURCES[browser]) {
                    this.lang = browser;
                }
            }
            this.resource = RESOURCES[this.lang];
            // Expose to window for Debugging/Console use
            window.Locale = this;
            window.locale = this; // [FIX] Expose lowercase for compatibility with legacy/refactored code
            console.log(`[Locale] Initialized: ${this.lang}`);
        } catch (e) {
            console.warn("[Locale] Init failed", e);
            this.lang = DEFAULT_LANG;
            this.resource = ko;
        }
    }

    /**
     * Get translated string
     * @param {string} key - Dot notation key (e.g. "menu.home")
     * @param {object} params - Replacement variables (e.g. { name: "John" })
     * @returns {string} Translated string or Key if missing
     */
    t(key, params = {}) {
        const keys = key.split('.');
        let value = this.resource;

        // Traverse object
        for (const k of keys) {
            if (value && value[k]) {
                value = value[k];
            } else {
                value = null;
                break;
            }
        }

        if (!value) {
            // Fallback to Code (optional: define fallback logic? or just return key)
            // For now, return key to make missing translations obvious in Dev
            return key;
        }

        // Interpolation: "Hello {name}" -> "Hello John"
        if (Object.keys(params).length > 0) {
            return value.replace(/{(\w+)}/g, (_, v) => params[v] !== undefined ? params[v] : `{${v}}`);
        }

        return value;
    }

    /**
     * Change Language
     * @param {string} lang - 'ko' or 'en'
     */
    setLanguage(lang) {
        if (!RESOURCES[lang]) {
            console.error(`[Locale] Unsupported language: ${lang}`);
            return;
        }
        this.lang = lang;
        this.resource = RESOURCES[lang];
        localStorage.setItem('mirinae_lang', lang);

        // [UX] Reload to apply changes globally
        window.location.reload();
    }

    getCurrentLanguage() {
        return this.lang;
    }

    /**
     * Format Date according to current locale
     * @param {Date|string|number} date 
     * @param {Intl.DateTimeFormatOptions} options 
     */
    formatDate(date, options = {}) {
        if (!date) return '';
        const d = new Date(date);
        // Default options if none provided
        const defaultOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };
        const finalOptions = Object.keys(options).length > 0 ? options : defaultOptions;

        try {
            return new Intl.DateTimeFormat(this.lang, finalOptions).format(d);
        } catch (e) {
            console.warn("[Locale] Date format failed", e);
            return d.toLocaleDateString();
        }
    }

    /**
     * Format Number according to current locale
     * @param {number} number 
     * @param {Intl.NumberFormatOptions} options 
     */
    formatNumber(number, options = {}) {
        if (typeof number !== 'number') return number;
        try {
            return new Intl.NumberFormat(this.lang, options).format(number);
        } catch (e) {
            console.warn("[Locale] Number format failed", e);
            return number.toString();
        }
    }

    /**
     * Update SEO Meta Tags
     * @param {object} meta 
     * @param {string} meta.title - Page Title key or raw string
     * @param {string} meta.description - Meta Description key or raw string
     */
    updateMeta({ title, description } = {}) {
        if (title) {
            // Try to translate if it's a key, otherwise use as is
            const translatedTitle = this.t(title) !== title ? this.t(title) : title;
            document.title = translatedTitle;
        }

        if (description) {
            const translatedDesc = this.t(description) !== description ? this.t(description) : description;
            let metaDesc = document.querySelector('meta[name="description"]');
            if (!metaDesc) {
                metaDesc = document.createElement('meta');
                metaDesc.name = "description";
                document.head.appendChild(metaDesc);
            }
            metaDesc.content = translatedDesc;

            // Also update OG description if needed
            let ogDesc = document.querySelector('meta[property="og:description"]');
            if (ogDesc) {
                ogDesc.content = translatedDesc;
            }
        }
    }
}

export const locale = new LocaleService();
locale.init();
